import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../lib/api';
import { getStatusLabel } from '../lib/status';

interface Proposal {
  id: string;
  title: string;
  department: string;
  status: string;
  submitter_name: string;
  submitted_at: string;
  urgency: string;
}

const REVIEW_STATUSES = 'status=submitted&status=under_triage&status=in_review&status=awaiting_deployment';

export function ReviewPage() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [needsMyRec, setNeedsMyRec] = useState(false);
  const [staleOnly, setStaleOnly] = useState(false);
  const [urgencyFilter, setUrgencyFilter] = useState<string>('all');

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(REVIEW_STATUSES);
      if (needsMyRec) params.set('needs_my_rec', 'true');
      if (staleOnly) params.set('stale', 'true');
      if (urgencyFilter !== 'all') params.set('urgency', urgencyFilter);

      const res = await api.get<{ proposals: Proposal[] }>(`/proposals?${params.toString()}`);
      setProposals(res.proposals);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [needsMyRec, staleOnly, urgencyFilter]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const isStale = (p: Proposal) => {
    if (!p.submitted_at || !['submitted', 'under_triage'].includes(p.status)) return false;
    const days = (Date.now() - new Date(p.submitted_at).getTime()) / (1000 * 60 * 60 * 24);
    return days > 7;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Review Queue</h1>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant={needsMyRec ? 'default' : 'outline'}
          size="sm"
          onClick={() => setNeedsMyRec(!needsMyRec)}
        >
          Needs my recommendation
        </Button>
        <Button
          variant={staleOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStaleOnly(!staleOnly)}
        >
          Stale (&gt;7 days)
        </Button>
        <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue placeholder="Urgency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All urgency</SelectItem>
            <SelectItem value="two_weeks">2 Weeks</SelectItem>
            <SelectItem value="one_month">1 Month</SelectItem>
            <SelectItem value="one_quarter">1 Quarter</SelectItem>
            <SelectItem value="no_deadline">No Deadline</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : proposals.length === 0 ? (
        <p className="text-muted-foreground">No proposals match the current filters.</p>
      ) : (
        <div className="border rounded-lg divide-y">
          {proposals.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-4">
              <div>
                <p className="font-medium">{p.title}</p>
                <p className="text-sm text-muted-foreground">
                  {p.submitter_name} · {p.department} · {p.urgency?.replace(/_/g, ' ')}
                  {isStale(p) && (
                    <span className="ml-2 text-amber-600 font-medium">Stale</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="info">{getStatusLabel(p.status)}</Badge>
                <Button size="sm" onClick={() => navigate(`/proposals/${p.id}`)}>
                  Review
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
